import {observer} from 'mobx-react-lite'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {FrameDebouncer} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {DragData as DragDataClass, DragDataObject} from '@common/drag-data'
import {TwDropPlaceholder} from '@components/common/base/table/containers'
import {DraggableNative} from '@components/draggable-native'
import {cn} from '@containers/html-utils'
import {TableRow} from './table-row'
import {useTableStore} from './table-store'
import {DragData, OnDrop, PrepareDragData} from './types'

interface TableBodyProps {
  stripeBackground?: boolean
  onDragStarted?: (rowId: DataObjectId | string) => void
  onDragEnded?: (transferData?: DragDataObject) => void
  noBorders?: boolean
  prepareDragData?: PrepareDragData
  onDrop?: OnDrop
  tableRowClassName?: string
  tableRowInnerClassName?: string
  isDropBlocked?: boolean
  highlightRowsAsDropzone?: boolean
  hideSubRowsOnDrag?: boolean
  isTableHeaderHidden?: boolean
  useNewPlaceholderLine?: boolean
}

// User can perform only one drag at a time, so we can store neighbours in a global variable
const dragStartNeighbours = ['', '']

// eslint-disable-next-line max-lines-per-function
export const TableBody = observer((props: TableBodyProps) => {
  const {stripeBackground = false, noBorders = false} = props
  const {
    tableRowClassName,
    tableRowInnerClassName,
    isDropBlocked,
    highlightRowsAsDropzone,
    hideSubRowsOnDrag,
    isTableHeaderHidden,
    useNewPlaceholderLine
  } = props
  const [state, store] = useTableStore()
  const currentOrder = useRef<(DataObjectId | string)[]>()
  const [dragData, setDragData] = useState<DragData>()
  const [placeholderTop, setPlaceholderTop] = useState<number | undefined>()
  const resetDragData = useCallback(() => {
    setDragData(undefined)
  }, [])

  useEffect(() => {
    if (state.containerEl && store.isDragTypeBetween) {
      if (!dragData) {
        setPlaceholderTop(undefined)
        return
      }

      const selector = `[data-row-id="${dragData.rowId}"]`
      const rowEl = state.containerEl.querySelector(selector)
      if (rowEl) {
        const containerRect = state.containerEl.getBoundingClientRect()
        const rowRect = (rowEl as HTMLElement).getBoundingClientRect()
        const top =
          dragData.position === 'before'
            ? rowRect.top - containerRect.top
            : rowRect.top + rowRect.height - containerRect.top
        // HACK: this might change depending on styles, not sure how to calculate
        const topPadding = isTableHeaderHidden ? 0 : 48
        setPlaceholderTop(top - topPadding)
      }
    }
  }, [
    setPlaceholderTop,
    dragData,
    state.containerEl,
    store.isDragTypeBetween,
    isTableHeaderHidden
  ])

  const onDragEnter = (
    e: React.DragEvent,
    currentId: DataObjectId | string
  ) => {
    e.preventDefault()

    if (
      store.isDragTypeOver &&
      state.draggingRowId &&
      state.draggingRowId !== currentId
    ) {
      const draggedIndex = state.order.indexOf(state.draggingRowId)
      const currentElementIndex = state.order.indexOf(currentId)
      const newOrder = [...state.order]
      newOrder.splice(draggedIndex, 1)
      newOrder.splice(currentElementIndex, 0, state.draggingRowId)
      currentOrder.current = newOrder
    }
  }

  let fallbackStopApplied = false
  const [dragDataDebouncer] = useState(() => new FrameDebouncer())

  const isVirtualized = !!state.virtualize
  const displayedRows = isVirtualized
    ? store.rows.slice(state.fromRow, state.toRow)
    : store.rows

  const onDragStarted = (rowId: DataObjectId | string) => {
    if (store.isDragTypeBetween && state.scrollableEl) {
      // HACK: prevents issues w/ hovered row background following drag scroll in Chrome
      // Raf is used to render bg for the dragged row image but not for table rows
      requestAnimationFrame(() => {
        state.scrollableEl?.classList.add('no-hover-bg')
      })
    }

    const draggedIndex = displayedRows.findIndex(row => row.data.id === rowId)
    dragStartNeighbours[0] =
      draggedIndex > 0 ? displayedRows[draggedIndex - 1].data.id : 'none'
    dragStartNeighbours[1] =
      draggedIndex < displayedRows.length - 1
        ? displayedRows[draggedIndex + 1].data.id
        : 'none'

    props.onDragStarted?.(rowId)
  }

  const onDragEnded = () => {
    if (store.isDragTypeOver && currentOrder.current && state.draggable) {
      store.setOrder(currentOrder.current)
    }

    if (store.isDragTypeBetween) {
      dragDataDebouncer.clear()
      if (fallbackStopApplied) {
        document.removeEventListener('mousemove', onDragEnded, {capture: true})
      }
      setDragData(undefined)
      setPlaceholderTop(undefined)

      // Removing the hack
      if (state.scrollableEl) {
        state.scrollableEl.classList.remove('no-hover-bg')
      }
    }

    props.onDragEnded?.()
  }

  const onDragOver = (e: React.DragEvent, rowId: DataObjectId | string) => {
    if (
      store.isDragTypeBetween &&
      e.target instanceof HTMLElement &&
      props.prepareDragData
    ) {
      if (!fallbackStopApplied) {
        document.addEventListener('mousemove', onDragEnded, {
          capture: true,
          passive: true,
          once: true
        })
        fallbackStopApplied = true
      }

      if (!isDropBlocked) {
        e.dataTransfer.dropEffect = 'move'
      }

      // Find the element with draggable attribute to get correct bounds
      let target = e.target as HTMLElement
      while (target && !target.getAttribute('draggable')) {
        if (target.parentElement === null) {
          return
        }
        target = target.parentElement
      }

      const {top, bottom} = target.getBoundingClientRect()
      const center = (top + bottom) / 2
      const before = e.clientY <= center
      const {draggingRowId} = state
      const {prepareDragData} = props
      const hidePlaceholder = before
        ? rowId === draggingRowId || rowId === dragStartNeighbours[1]
        : rowId === draggingRowId || rowId === dragStartNeighbours[0]

      dragDataDebouncer.runLatest(
        hidePlaceholder
          ? resetDragData
          : () => {
              setDragData(prepareDragData(draggingRowId, rowId, before))
            }
      )
    }
  }

  const onDragLeave = (rowId: DataObjectId | string) => {
    if (store.isDragTypeBetween) {
      dragDataDebouncer.runLatest(() => {
        setDragData(data => (data?.rowId === rowId ? undefined : data))
      })
    }
  }

  const onDrop = (
    event: React.DragEvent<HTMLDivElement>,
    rowId: DataObjectId | string
  ): void => {
    const draggingData = DragDataClass.get()

    // NB! draggingData.id -> means that the element is dragging from outside the current table and current rowId
    if (
      !state.draggingRowId &&
      draggingData?.type === 'task' &&
      draggingData.id
    ) {
      props.onDrop?.(event, draggingData.id, rowId, false, draggingData.listId)
      return
    }

    if (!store.isDragTypeBetween) {
      return
    }

    if (dragData) {
      setDragData(undefined)
    }

    if (state.draggingRowId && dragData) {
      props.onDrop?.(
        event,
        state.draggingRowId,
        rowId,
        dragData.position === 'before'
      )
      props.onDragEnded?.()
    }
  }

  const isDraggingBetween = store.isDragTypeBetween && !!state.draggingRowId

  return (
    <>
      <TwDropPlaceholder
        style={{transform: `translate(0px,${placeholderTop}px)`}}
        $isNew={useNewPlaceholderLine}
        visible={
          !isDropBlocked &&
          !!dragData?.rowId &&
          typeof placeholderTop !== 'undefined'
        }
      />
      {displayedRows.map(
        (
          {
            data,
            transferData,
            onClick,
            className = '',
            subRows,
            collapsable,
            columnsWrapperClassName,
            draggable,
            draggableOut,
            hoverable
          },
          ix
        ) => {
          const i = isVirtualized ? state.fromRow + ix : ix
          let isDraggable =
            (state.draggable && !state.sort) || !!state.isDraggableOut
          if (typeof draggable !== 'undefined') {
            isDraggable = draggable
          }
          let isDraggableOut = state.isDraggableOut
          if (typeof draggableOut !== 'undefined') {
            isDraggableOut = draggableOut
          }
          let style: React.CSSProperties | undefined
          if (isVirtualized) {
            const top = state.virtualize?.rowToPosition(i) ?? 0
            style = {
              transform: `translate(0px,${top}px)`,
              position: 'absolute'
              // width: '100%'
            }
          }

          return (
            <DraggableNative
              key={i}
              draggable={isDraggable}
              transfer={transferData ?? {type: 'empty'}}
              onDragStarted={() => onDragStarted(data.id)}
              onDragEnded={
                isDraggable || isDraggableOut ? onDragEnded : undefined
              }
              onDragEnter={e => onDragEnter(e, data.id)}
              onDragOver={e => {
                e.preventDefault()
                onDragOver(e, data.id)
              }}
              onDragLeave={() => onDragLeave(data.id)}
              onDrop={event => onDrop(event, data.id)}
              style={style}
            >
              <div
                className={cn(
                  'relative',
                  className,
                  stripeBackground && i % 2 !== 0 && 'bg-line-disabled',
                  isDraggingBetween && 'pointer-events-none'
                )}
                key={i}
                style={!isDraggable ? style : undefined}
              >
                <TableRow
                  rowIndex={`${i}`}
                  data={data}
                  onClick={onClick}
                  subRows={subRows}
                  hideSubRowsOnDrag={hideSubRowsOnDrag}
                  collapsable={collapsable}
                  draggable={isDraggable}
                  isDraggableOut={isDraggableOut}
                  hoverable={isDraggable || hoverable}
                  columnsWrapperClassName={columnsWrapperClassName}
                  noBorders={noBorders}
                  className={tableRowClassName}
                  innerClassName={tableRowInnerClassName}
                  highlightRowsAsDropzone={highlightRowsAsDropzone}
                />
              </div>
            </DraggableNative>
          )
        }
      )}
    </>
  )
})
